import {colors} from 'shells/colors/colors';
import {AccountIcon} from 'shells/icon/svg/account-icon';
import {ContactIcon} from 'shells/icon/svg/contact-icon';
import {LeadIcon} from 'shells/icon/svg/lead-icon';
import {Link} from 'shells/link/link';
import {Body} from 'shells/typography/body';

import type {EntityType} from 'nutshell-graphql-types';
import type {
    GetSubmissions_submissions_submissions_edges_node as Submission,
    GetSubmissions_submissions_submissions_edges_node_fields_edges as SubmissionFieldsEdge,
    MultiPageFormFragment,
    FormFieldFragment,
} from 'nutshell-graphql-types';

import {fieldsFromPublishedVersionPages} from '../helpers';

import {SUBMISSION_TABLE_INPUT_TYPE_SET} from './consts';

export function getConditionalColumns(form: MultiPageFormFragment) {
    // @ts-expect-error
    const columns = [];

    if (form.publishedVersion) {
        fieldsFromPublishedVersionPages(form.publishedVersion).forEach((field) => {
            const entityIcon = getEntityIcon(field.entityName);

            if (SUBMISSION_TABLE_INPUT_TYPE_SET.has(field.type)) {
                const column = {
                    id: field.id,
                    Header: (
                        <div className='flex gap-8 align-center'>
                            {entityIcon && entityIcon}
                            <span title={field.label}>
                                {field.label}
                                {field.isRequired && (
                                    <Body size='small' color='error'>
                                        {' '}
                                        *
                                    </Body>
                                )}
                            </span>
                        </div>
                    ),
                    accessor: (submission: Submission) => submission,
                    dataType: 'component',
                    Cell: ({cell}: any) => {
                        // @ts-expect-error
                        const submissionField = cell.value.fields.edges.find((edge) =>
                            shouldMapField(edge, field)
                        );

                        if (submissionField) {
                            return getCellValue(submissionField);
                        }

                        return '';
                    },
                    minWidth: 125,
                    width: 175,
                    maxWidth: 500,
                    shouldTruncate: true,
                    disableSortBy: true,
                } as const;

                columns.push(column);
            }
        });
    }

    // @ts-expect-error
    return columns;
}

export function getCompletionPercentage(submission: Submission): number {
    const totalFields = submission.fields.totalCount;
    const completedFields = submission.fields.edges.filter((edge) => edge.fieldValue).length;

    if (totalFields === 0) return 0;

    return completedFields / totalFields;
}

function getEntityIcon(entityName?: EntityType | null) {
    let Icon = null;
    let color;
    switch (entityName) {
        case 'CONTACT':
            Icon = ContactIcon;
            color = colors.person;
            break;
        case 'ACCOUNT':
            Icon = AccountIcon;
            color = colors.company;
            break;
        case 'LEAD':
            Icon = LeadIcon;
            break;
        default:
            return null;
    }

    // @ts-expect-error
    return <Icon size={13} alignCenter={true} color={color} />;
}

function getCellValue(submissionField: SubmissionFieldsEdge): string | React.ReactNode {
    if (submissionField.fieldValue) {
        if (submissionField.fieldValue.__typename === 'FormFieldSubmissionValueSingle') {
            const value = submissionField.fieldValue.value;
            const fieldName = submissionField.node.fieldName;

            if (fieldName === 'email' || fieldName === 'url') {
                return (
                    <Link
                        // @ts-expect-error
                        title={value}
                        href={fieldName === 'email' ? `mailto:${value}` : value}
                        variant='primary'
                        size='small'
                        shouldAddHttpProtocol={fieldName === 'url'}
                        newTab={fieldName === 'url'}
                    >
                        {value}
                    </Link>
                );
            }

            return <div title={value}>{value}</div>;
        } else if (submissionField.fieldValue.__typename === 'FormFieldSubmissionValueMultiple') {
            const values = submissionField.fieldValue.values.join(', ');

            return <div title={values}>{values}</div>;
        }
    }

    return '';
}

function shouldMapField(
    submissionField: SubmissionFieldsEdge,
    publishedField: FormFieldFragment
): boolean {
    if (submissionField.node.label === publishedField.label) {
        return true;
    } else if (
        submissionField.node.originalFieldId &&
        publishedField.originalFieldId &&
        submissionField.node.originalFieldId === publishedField.originalFieldId
    ) {
        return true;
    }

    return false;
}
